import { Eye } from 'lucide-react';
import { useCollaborationStore } from '@/stores/collaborationStore';
import { useMapStore } from '@/stores/mapStore';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { User } from '@/types/collaboration';

export function UsersPanel() {
  const { localUser, remoteUsers } = useCollaborationStore();
  const { isFollowingUser, setFollowingUser } = useMapStore();

  const handleFollow = (userId: string) => {
    setFollowingUser(isFollowingUser === userId ? null : userId);
  };

  return (
    <div className="p-3">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          Users
        </h3>
        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
          {remoteUsers.length + 1} online
        </Badge>
      </div>

      <div className="space-y-1">
        {localUser && (
          <UserItem user={localUser} isLocal activeTool={null} isFollowing={false} onFollow={() => {}} />
        )}

        {remoteUsers.map((ru) => (
          <UserItem
            key={ru.clientId}
            user={ru.user}
            isLocal={false}
            activeTool={ru.activeTool}
            isFollowing={isFollowingUser === ru.user.id}
            onFollow={() => handleFollow(ru.user.id)}
          />
        ))}

        {remoteUsers.length === 0 && (
          <p className="text-center py-6 text-xs text-muted-foreground">
            No other users connected
          </p>
        )}
      </div>
    </div>
  );
}

function UserItem({
  user,
  isLocal,
  activeTool,
  isFollowing,
  onFollow,
}: {
  user: User;
  isLocal: boolean;
  activeTool: string | null;
  isFollowing: boolean;
  onFollow: () => void;
}) {
  return (
    <div className={cn(
      'flex items-center gap-2.5 p-2 rounded-md transition-colors',
      'hover:bg-accent/50',
      isLocal && 'ring-1 ring-primary/20'
    )}>
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-medium text-white flex-shrink-0"
        style={{ backgroundColor: user.color }}
      >
        {user.name.charAt(0).toUpperCase()}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-foreground truncate">{user.name}</span>
          {isLocal && <span className="text-[10px] text-muted-foreground">(you)</span>}
        </div>
        {activeTool && (
          <span className="text-[10px] text-muted-foreground capitalize">
            Drawing: {activeTool}
          </span>
        )}
      </div>

      {!isLocal && (
        <button
          onClick={onFollow}
          className={cn(
            'p-1 rounded transition-colors',
            isFollowing
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-accent'
          )}
          title={isFollowing ? 'Stop following' : 'Follow user'}
        >
          <Eye size={13} />
        </button>
      )}
    </div>
  );
}
